import React from 'react';
import { motion } from 'framer-motion';
import { Crown, Fish, Beef, Leaf } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ZoomableImage } from './ZoomableImage';

const SECTIONS = [
  { key: 'mer', label: 'Gastronomie de la Mer', icon: Fish },
  { key: 'terroirs', label: 'Excellence des Terroirs', icon: Beef },
  { key: 'vegetale', label: 'Quintessence Végétale', icon: Leaf },
];

export const MenuCardMenusPrestige = ({ dishes }) => {
  const { addToCart } = useCart();

  const handleAddToCart = async (dish) => {
    try {
      await addToCart(dish.dish_id, 1);
      toast.success(`${dish.name} ajouté au panier`);
    } catch (error) {
      toast.error('Connectez-vous pour commander');
    }
  };

  // Group dishes by mois
  const moisGroups = {};
  dishes.forEach((d) => {
    const key = d.mois || 'mois_1';
    if (!moisGroups[key]) moisGroups[key] = { label: d.mois_label || '', items: [] };
    moisGroups[key].items.push(d);
  });

  const moisRomanMap = { mois_1: 'I', mois_2: 'II', mois_3: 'III' };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Elegant Header — Navy / Gold style matching menu image */}
      <div className="bg-[#1A2A3D] rounded-2xl px-8 py-10 text-center mb-10 border-b-4 border-[#D4AF37] shadow-xl">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="w-12 h-[1px] bg-[#D4AF37]"></span>
            <Crown className="text-[#D4AF37]" size={26} />
            <span className="w-12 h-[1px] bg-[#D4AF37]"></span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#D4AF37] tracking-widest uppercase">
            Menus Prestige
          </h2>
          <p className="text-white/85 mt-3 italic font-heading text-lg">
            Une Collection Gastronomique sur 3 Mois
          </p>
        </motion.div>
      </div>

      {/* Menu Card Container */}
      <div className="bg-[#FAF8F4] rounded-3xl p-8 md:p-12 shadow-card border border-[#D4C4AA]/30 relative">
        {Object.entries(moisGroups).map(([moisKey, group]) => (
          <motion.section
            key={moisKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 last:mb-0"
          >
            {/* Mois title */}
            <div className="text-center mb-8">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2A3D] tracking-wide">
                <span className="italic">Mois {moisRomanMap[moisKey] || moisKey}</span>
                {group.label && <span> : {group.label}</span>}
              </h3>
              <div className="w-32 h-[2px] bg-[#D4AF37] mx-auto mt-3"></div>
            </div>

            {/* Sections */}
            {SECTIONS.map(({ key, label, icon: Icon }) => {
              const sectionDishes = group.items.filter((d) => d.formule_type === key);
              if (sectionDishes.length === 0) return null;
              return (
                <div key={key} className="mb-8 last:mb-0">
                  <div className="flex items-center justify-center gap-3 mb-5">
                    <span className="text-[#D4AF37] text-lg">—</span>
                    <Icon className="text-[#D4AF37]" size={18} />
                    <h4 className="font-heading text-sm md:text-base font-bold text-[#D4AF37] tracking-[0.25em] uppercase">
                      {label}
                    </h4>
                    <Icon className="text-[#D4AF37]" size={18} />
                    <span className="text-[#D4AF37] text-lg">—</span>
                  </div>
                  {sectionDishes.map((dish) => (
                    <div
                      key={dish.dish_id}
                      onClick={() => handleAddToCart(dish)}
                      className="group py-4 px-6 -mx-6 rounded-2xl hover:bg-[#1A2A3D]/5 transition-all cursor-pointer text-center mb-2"
                      data-testid={`prestige-item-${dish.dish_id}`}
                    >
                      {dish.image_url && (
                        <div className="mb-4 rounded-2xl overflow-hidden max-w-md mx-auto shadow-md">
                          <ZoomableImage
                            src={dish.image_url}
                            alt={dish.name}
                            className="w-full h-auto aspect-[16/9] object-cover"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="flex items-baseline justify-center gap-3 flex-wrap">
                        <h5 className="font-heading text-lg md:text-xl font-bold text-[#1A2A3D] group-hover:text-[#D4AF37] transition-colors">
                          {dish.name}
                        </h5>
                      </div>
                      <p className="text-[#6D4C41] text-sm mt-2 italic leading-relaxed max-w-2xl mx-auto">
                        {dish.description}
                      </p>
                      <p className="text-xs text-[#D4AF37] mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        + Ajouter au panier
                      </p>
                    </div>
                  ))}
                </div>
              );
            })}
          </motion.section>
        ))}
      </div>

      <div className="text-center mt-8">
        <p className="text-[#6D4C41]/70 text-sm italic">
          Cliquez sur un plat pour l'ajouter au panier
        </p>
        <p className="text-[#6D4C41]/50 text-xs mt-2">
          Composition signée par notre Chef • Sur commande 48h à l'avance
        </p>
      </div>
    </div>
  );
};
